import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api, type PublicUser, type Uuid } from "@shared/api";
import { useMe, useSession } from "@entities/session";
import { Mark, PRESENCE_LABELS, PRESENCE_ORDER, fromSelf, markOf } from "@entities/user";
import { Markup } from "@shared/ui/Markup";
import { Menu, SEPARATOR, type MenuItem } from "@shared/ui/Menu";
import { Popover, PopoverAnchor, PopoverContent } from "@shared/ui/popover";
import { useDeveloper } from "@shared/ui/developer";
import { IconChevronRight, IconCopy, IconEdit, IconMembers, IconPlus } from "@shared/ui/icons";
import { randomPrompt } from "@features/custom-status";
import { Avatar, Badges, Banner } from "./parts";

/** Что значит присутствие — второй строкой в выборе, как в Discord; правило оклика — `notify-rule`. */
const PRESENCE_NOTES: Partial<Record<keyof typeof PRESENCE_LABELS, string>> = {
  focus: "Оповещения — только об упоминаниях",
  busy: "Вы не будете получать уведомления на рабочем столе",
  invisible: "У вас будет статус «Не в сети»",
};

const capital = (text: string) => text[0].toUpperCase() + text.slice(1);

/**
 * Своя карточка по щелчку на себе внизу — как в Discord («Управление профилем
 * и статусом»): баннер, лицо, пузырь статуса, имя, а под ними пункты —
 * «Редактировать профиль», присутствие со своим выбором, переключение учётных
 * записей. Встаёт над полосой себя и едет за ней.
 */
export function SelfPopout({
  anchor,
  onClose,
  onOpenFull,
  onEditProfile,
  onSetStatus,
}: {
  anchor: HTMLElement;
  onClose: () => void;
  /** Свой полный профиль — окном. */
  onOpenFull: (userId: Uuid) => void;
  onEditProfile: () => void;
  /** Свой статус — окно «Настроить статус»; подсказка — та, что была в пузыре. */
  onSetStatus: (prompt: string) => void;
}) {
  const me = useMe();
  const refreshMe = useSession((state) => state.refreshMe);
  const developer = useDeveloper((state) => state.on);
  const [presenceAt, setPresenceAt] = useState<{ x: number; y: number } | null>(null);
  const [prompt] = useState(randomPrompt);
  const virtual = useMemo(() => ({ current: anchor }), [anchor]);

  // Части карточки ждут человека с сервера; свой — тот же, только «я».
  const user = { ...me, relationship: "myself", online: true } as PublicUser;
  const person = fromSelf(me);
  const presence = me.status_presence ?? "online";
  const hasStatus = !!(me.status_text || me.status_emoji);
  const full = () => {
    onClose();
    onOpenFull(me.id);
  };

  const presenceItems: MenuItem[] = PRESENCE_ORDER.flatMap((value, index) => {
    const item: MenuItem = {
      label: capital(PRESENCE_LABELS[value]),
      note: PRESENCE_NOTES[value],
      choice: true,
      on: presence === value,
      onPick: () =>
        void api.users
          .updateProfile({ status_presence: value })
          .then(() => refreshMe())
          .catch(() => {}),
    };
    // Как в Discord: «В сети» — отдельно от остальных.
    return index === 1 ? [SEPARATOR, item] : [item];
  });

  return (
    <Popover open onOpenChange={(open) => !open && onClose()}>
      <PopoverAnchor virtualRef={virtual} />
      <PopoverContent
        side="top"
        align="start"
        sideOffset={8}
        collisionPadding={12}
        aria-label="Управление профилем и статусом"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => {
          // Щелчок по себе ещё раз закрывает карточку им же, а меню присутствия — её часть.
          const target = event.target as Node | null;
          if (anchor.contains(target) || (presenceAt && target instanceof Element && target.closest("[role='menu']")))
            event.preventDefault();
        }}
      >
        <div className="profile-pop surface-pop self-pop">
          <div className="profile-pop-scroll">
            <Banner user={user} />
            <Avatar user={user} person={person} size={80} hidden={false} onOpen={full} />
            {/* Пузырь статуса у лица — как в Discord: свой статус или подсказка задать его. */}
            <button
              type="button"
              className={`self-pop-bubble ${hasStatus ? "" : "prompt"}`}
              onClick={() => {
                onClose();
                onSetStatus(prompt);
              }}
            >
              {hasStatus ? (
                <>
                  {me.status_emoji && <span className="status-mark">{me.status_emoji}</span>}
                  <span className="self-pop-bubble-text">{me.status_text}</span>
                </>
              ) : (
                <>
                  <IconPlus size={12} />
                  <span className="self-pop-bubble-text">{prompt}</span>
                </>
              )}
            </button>

            <div className="profile-pop-body">
              <div>
                <button type="button" className="profile-title profile-title-link" onClick={full}>
                  <span className="ellipsis">{person.name}</span>
                </button>
                <div className="profile-sub">
                  <span className="ellipsis">{me.handle}</span>
                  {me.pronouns && (
                    <>
                      <span className="profile-shared-dot" aria-hidden />
                      <span className="ellipsis">{me.pronouns}</span>
                    </>
                  )}
                  <Badges user={user} />
                </div>
              </div>
              {me.bio && (
                <div className="profile-about">
                  <Markup text={me.bio} nameOf={() => null} mine="" />
                </div>
              )}

              <div className="self-pop-card">
                <button
                  type="button"
                  className="self-pop-item"
                  onClick={() => {
                    onClose();
                    onEditProfile();
                  }}
                >
                  <IconEdit size={16} />
                  <span className="grow">Редактировать профиль</span>
                </button>
                <button
                  type="button"
                  className={`self-pop-item ${presenceAt ? "on" : ""}`}
                  aria-haspopup="menu"
                  aria-expanded={!!presenceAt}
                  onClick={(event) => {
                    const box = event.currentTarget.getBoundingClientRect();
                    setPresenceAt(presenceAt ? null : { x: box.right + 8, y: box.top });
                  }}
                >
                  <span className="self-pop-mark">
                    <Mark kind={markOf(presence)} size={10} />
                  </span>
                  <span className="grow">{capital(PRESENCE_LABELS[presence])}</span>
                  <IconChevronRight size={14} />
                </button>
              </div>

              <div className="self-pop-card">
                {/* Нескольких учётных записей у нас пока нет — пункт на месте, планом. */}
                <button type="button" className="self-pop-item" disabled>
                  <IconMembers size={16} />
                  <span className="grow">Переключение между учётными записями</span>
                  <span className="plan-tag">план</span>
                </button>
                {developer && (
                  <button
                    type="button"
                    className="self-pop-item"
                    onClick={() => {
                      void navigator.clipboard?.writeText(me.id).catch(() => {});
                      onClose();
                    }}
                  >
                    <IconCopy size={16} />
                    <span className="grow">Копировать ID пользователя</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        {presenceAt &&
          createPortal(
            <Menu title="СТАТУС" at={presenceAt} items={presenceItems} onClose={() => setPresenceAt(null)} />,
            document.body,
          )}
      </PopoverContent>
    </Popover>
  );
}
